"use client";

import { ArrowUp } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

/**
 * The ask box as markup: the question field, the sources your Twin can use,
 * the send button, and one-click suggestions under it. AskComposer holds the
 * question and sends it; without handlers the box shows as it rests, empty.
 */
export function AskComposerView({
  label,
  placeholder,
  suggestions = [],
  question = "",
  asking = false,
  sources,
  onQuestionChange,
  onAsk,
}: {
  label: string;
  placeholder: string;
  suggestions?: readonly string[];
  question?: string;
  /** A question is being sent; the box waits. */
  asking?: boolean;
  /** The sources row (ComposerSources). */
  sources?: ReactNode;
  onQuestionChange?: (question: string) => void;
  onAsk?: (text: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      <form
        className="mlx-prompt-composer chat-composer-shadow w-full rounded-xl border border-transparent bg-control"
        onSubmit={(event) => {
          event.preventDefault();
          onAsk?.(question);
        }}
      >
        <Textarea
          rows={1}
          aria-label={label}
          placeholder={placeholder}
          {...(onQuestionChange
            ? {
                value: question,
                onChange: (event) => onQuestionChange(event.target.value),
              }
            : { defaultValue: question })}
          disabled={asking}
          className="mlx-composer-textarea max-h-40 min-h-9 resize-none px-4 pt-2.5 pb-0 leading-6 [field-sizing:content] disabled:bg-transparent"
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              onAsk?.(question);
            }
          }}
        />
        <div className="mlx-prompt-footer min-h-10 pt-1 pb-2">
          <div className="flex min-w-0 items-center gap-1">{sources}</div>
          <Button
            type="submit"
            size="icon"
            disabled={asking || !question.trim()}
            className="size-8 shrink-0 rounded-full"
            aria-label={asking ? "Asking…" : "Ask"}
          >
            {asking ? <Spinner aria-hidden /> : <ArrowUp className="size-4" aria-hidden />}
          </Button>
        </div>
      </form>
      {suggestions.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Suggested questions">
          {suggestions.map((suggestion) => (
            <li key={suggestion}>
              <Button
                type="button"
                size="xs"
                variant="outline"
                className="font-normal text-muted-foreground hover:text-foreground"
                disabled={asking}
                onClick={() => onAsk?.(suggestion)}
              >
                {suggestion}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
